import { useEffect, useMemo, useRef } from 'react';
import type { Control, FieldValues, Path, RegisterOptions } from 'react-hook-form';
import { useController, useFormContext } from 'react-hook-form';

import VNDropdownBase from './VNDropdownBase';
import type { VNDropdownBaseProps } from './VNDropdownBase';

export interface VNDropdownRhfProps<TFieldValues extends FieldValues = FieldValues>
  extends Omit<VNDropdownBaseProps, 'value' | 'onChange' | 'onBlur' | 'error' | 'inputRef'> {
  name: Path<TFieldValues>;
  control?: Control<TFieldValues>;
  rules?: RegisterOptions<TFieldValues, Path<TFieldValues>>;
  required?: boolean;
  onValueChange?: (value: VNDropdownBaseProps['value']) => void;
  /** Called when the list closes, after the field `onBlur` handler runs. */
  onAfterBlur?: () => void;
}

const VNDropdownRhf = <TFieldValues extends FieldValues = FieldValues>({
  name,
  control: controlProp,
  rules,
  required,
  label,
  multiple,
  onValueChange,
  onAfterBlur,
  ...baseProps
}: VNDropdownRhfProps<TFieldValues>) => {
  const formContext = useFormContext<TFieldValues>();
  const control = controlProp ?? formContext?.control;

  const requiredMessage = required ? `${label || name} is required` : undefined;
  const resolvedRules = useMemo(
    () =>
      ({
        ...(rules || {}),
        // Always set the key so flipping `required` off also clears a previously
        // registered required rule (register merges options onto the existing field).
        required: requiredMessage ?? rules?.required,
      }) as RegisterOptions<TFieldValues, Path<TFieldValues>>,
    [rules, requiredMessage]
  );

  const {
    field: { value, onChange, onBlur, ref },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: resolvedRules,
    defaultValue: (multiple ? [] : null) as never,
  });

  // useController keeps the rules it registered on mount, so re-register when the
  // required state changes to keep validation in sync with the current props.
  const rulesRef = useRef(resolvedRules);
  rulesRef.current = resolvedRules;
  useEffect(() => {
    control?.register(name, rulesRef.current);
  }, [control, name, requiredMessage]);

  const handleChange = (next: VNDropdownBaseProps['value']) => {
    if (onValueChange) {
      onValueChange(next);
      return;
    }
    onChange(next);
  };

  const handleBlur = () => {
    onBlur();
    onAfterBlur?.();
  };

  return (
    <VNDropdownBase
      {...baseProps}
      name={name}
      label={label}
      multiple={multiple}
      value={value ?? (multiple ? [] : null)}
      onChange={handleChange}
      onBlur={handleBlur}
      error={error?.message}
      inputRef={ref}
    />
  );
};

export default VNDropdownRhf;
